// GoogleCalendarConnect — Admin hub card for the builds calendar (Oct 16, 2026).
//
// Shows whether the CRM can edit "Team Alex Builds", which account it acts as,
// lets an admin paste the calendar id, and starts the one-time Google sign-in
// (OAuth) when that is the chosen mode. Service-account mode needs no button:
// the key sits in the vault and the card just reports it.

// Oct 28, 2026: an Other Trades section. The subs' visits (gutters, guards) go on that calendar through the
// trade sheet; this card reports it and links the events Robert typed there by hand to their jobs on launch
// (a preview first, then for real), so the Builds board's Trades view starts full.

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { buildCalendar, fetchCalendarStatus, type CalendarStatus } from "../../lib/buildCalendar";
import { tradeCall } from "../../lib/trades";

interface LinkRow { event: string; date: string; result: string; why?: string; deal?: string; work?: string; vendor?: string | null }
interface LinkReport { ok: boolean; dry_run: boolean; events: number; report: LinkRow[] }

const LINK_RESULT: Record<string, string> = {
  would_link: "Will link", linked: "Linked", already_linked: "Already linked", skipped: "Skipped", failed: "Failed",
};

export default function GoogleCalendarConnect() {
  const [status, setStatus] = useState<CalendarStatus | null>(null);
  const [calId, setCalId] = useState("");
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  async function load() {
    try {
      const s = await fetchCalendarStatus();
      setStatus(s);
      setCalId(s.calendar_id ?? "");
    } catch (e) {
      setBanner({ kind: "err", text: (e as Error).message });
    }
  }

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const r = p.get("gcal_oauth");
    if (r === "success") setBanner({ kind: "ok", text: `Google Calendar connected${p.get("as") ? ` as ${p.get("as")}` : ""}. Builds will be created and rescheduled on the calendar from here on.` });
    if (r === "error") {
      const d = p.get("detail") ?? "unknown";
      setBanner({ kind: "err", text: d === "invalid_state"
        ? "Security check failed (the link expired or was already used). Click Connect again."
        : d === "no_refresh_token"
        ? "Google didn't hand back a long-lived token. Click Connect again and approve the access prompt."
        : `Google connection failed: ${d}` });
    }
    void load();
  }, []);

  async function saveCalendarId() {
    setBusy(true);
    setBanner(null);
    const v = calId.trim();
    const { error } = await supabase.from("finance_settings")
      // value_jsonb is NOT NULL: an empty string means "off" (the edge function
      // treats "" the same as unset).
      .upsert({ key: "google_calendar_builds_id", value_jsonb: v ? v : "", updated_at: new Date().toISOString() }, { onConflict: "key" });
    if (error) setBanner({ kind: "err", text: `Couldn't save: ${error.message}` });
    else { setBanner({ kind: "ok", text: v ? "Calendar id saved." : "Calendar id cleared — the integration is off." }); await load(); }
    setBusy(false);
  }

  async function connect() {
    setBusy(true);
    setBanner(null);
    try {
      const { url } = await buildCalendar<{ ok: boolean; url: string }>({ action: "connect_url" });
      window.location.href = url;
    } catch (e) {
      setBanner({ kind: "err", text: (e as Error).message });
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!window.confirm("Disconnect Google Calendar? Add to Calendar goes back to the template link until it is reconnected.")) return;
    setBusy(true);
    try { await buildCalendar({ action: "disconnect" }); await load(); setBanner({ kind: "ok", text: "Disconnected." }); }
    catch (e) { setBanner({ kind: "err", text: (e as Error).message }); }
    setBusy(false);
  }

  // Other Trades: link Robert's hand-made events to their jobs. A dry run shows the list first.
  const [linkReport, setLinkReport] = useState<LinkReport | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);

  async function linkExisting(dryRun: boolean) {
    if (!dryRun && !window.confirm("Link these events to their jobs? Each one becomes a trade on the Builds board (no new Google events, no texts).")) return;
    setLinkBusy(true);
    setBanner(null);
    try {
      const r = await tradeCall<LinkReport>({ action: "trade_link_existing", dry_run: dryRun });
      setLinkReport(r);
      if (!dryRun) {
        const n = r.report.filter((x) => x.result === "linked").length;
        setBanner({ kind: "ok", text: `${n} event${n === 1 ? "" : "s"} linked. They're on the Builds board's Trades view now.` });
      }
    } catch (e) {
      setBanner({ kind: "err", text: (e as Error).message });
    }
    setLinkBusy(false);
  }

  const modeLabel = status?.mode === "oauth" ? "Google sign-in (OAuth)" : status?.mode === "service_account" ? "Service account" : "Not connected";

  return (
    <div className="space-y-3">
      {banner && (
        <div className={`rounded-lg px-3 py-2 text-sm ${banner.kind === "ok" ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>{banner.text}</div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-700">
          Lets the PM Board create builds on <strong>Team Alex Builds</strong> and edit the event when a build is rescheduled.
        </div>
        {status && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${status.configured ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
            {status.configured ? "Connected" : "Not connected"}
          </span>
        )}
      </div>

      {status && (
        <div className="text-xs text-slate-600 space-y-0.5">
          <div>Sign-in: {modeLabel}{status.connected_as ? ` · acting as ${status.connected_as}` : ""}</div>
          {status.mode === "service_account" && !status.act_as && <div className="text-rose-700">google_calendar_act_as is empty — set the Workspace user the CRM acts as.</div>}
          {status.last_error && <div className="text-rose-700">Last Google error: {status.last_error}</div>}
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Team Alex Builds calendar id</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={calId}
            onChange={(e) => setCalId(e.target.value)}
            placeholder="c_…@group.calendar.google.com"
            className="flex-1 border border-slate-300 rounded px-3 py-1.5 text-sm font-mono"
          />
          <button onClick={saveCalendarId} disabled={busy} className="px-3 py-1.5 rounded bg-slate-800 text-white text-sm font-semibold disabled:opacity-50">Save</button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">Google Calendar → Settings → Team Alex Builds → "Integrate calendar" → Calendar ID.</p>
      </div>

      {status?.mode !== "service_account" && (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={connect} disabled={busy || !status?.oauth_client_ready}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-50">
            {status?.mode === "oauth" ? "Reconnect Google Calendar" : "Connect Google Calendar"}
          </button>
          {status?.mode === "oauth" && (
            <button onClick={disconnect} disabled={busy} className="text-xs text-slate-500 underline disabled:opacity-50">Disconnect</button>
          )}
          {status && !status.oauth_client_ready && (
            <span className="text-[11px] text-slate-500">Needs google_oauth_client_id + google_oauth_client_secret in the vault first.</span>
          )}
        </div>
      )}
      <p className="text-[11px] text-slate-500">
        Whoever clicks Connect is the account the CRM acts as — Robert, so events stay his. Redirect URI for the Google OAuth client: <span className="font-mono">{status?.redirect_uri ?? "…"}</span>
      </p>

      <div className="border-t border-slate-200 pt-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm text-slate-700">
            <strong>Other Trades</strong>: the subs' visits (gutters, guards, soffit and fascia, detach and reset) from the trade sheet.
          </div>
          {status && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${status.trades_calendar_id && status.configured ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
              {status.trades_calendar_id && status.configured ? "Connected" : "Not connected"}
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-500">
          Robert's hand-made events on Other Trades (today onward, next 60 days) can be linked to their jobs by street, so they show on the Builds board. Preview first; nothing changes on Google either way.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => void linkExisting(true)} disabled={linkBusy || !status?.trades_calendar_id}
                  className="px-3 py-1.5 rounded bg-slate-800 text-white text-sm font-semibold disabled:opacity-50">
            {linkBusy ? "Working…" : "Preview the events to link"}
          </button>
          {linkReport?.dry_run && linkReport.report.some((x) => x.result === "would_link") && (
            <button onClick={() => void linkExisting(false)} disabled={linkBusy}
                    className="px-3 py-1.5 rounded bg-bardin-orange text-white text-sm font-semibold disabled:opacity-50">
              Link {linkReport.report.filter((x) => x.result === "would_link").length} to their jobs
            </button>
          )}
        </div>
        {linkReport && (
          <div className="text-xs">
            <div className="text-slate-600 mb-1">{linkReport.events} event{linkReport.events === 1 ? "" : "s"} on Other Trades from today.{linkReport.dry_run ? " Nothing linked yet." : ""}</div>
            {linkReport.report.length > 0 && (
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {linkReport.report.map((r, i) => (
                  <div key={i} className="px-2 py-1.5 flex flex-wrap gap-x-2 gap-y-0.5">
                    <span className={`font-semibold shrink-0 ${r.result === "failed" ? "text-rose-700" : r.result === "skipped" ? "text-slate-500" : "text-emerald-700"}`}>{LINK_RESULT[r.result] ?? r.result}</span>
                    <span className="text-slate-700">{r.date} · {r.event}</span>
                    {r.deal && <span className="text-slate-500">→ {r.deal}{r.work ? ` (${r.work.replace("_", " ")})` : ""}{r.vendor ? ` · ${r.vendor}` : ""}</span>}
                    {r.why && <span className="text-slate-500">({r.why})</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
